"use client";

import { useEffect } from 'react';
import { DEFAULT_LOCALE, LOCALES } from '@lib/constants';
import { syncLocaleStorage, setStoredLocale } from '@lib/util/locale-storage';
/**
 * LocaleHandler component manages locale synchronization between:
 * - URL parameters (from next-intl routing)
 * - localStorage (for client-side persistence)
 * - Document attributes (for accessibility)
 * - Cookies (for server-side consistency)
 */
export default function LocaleHandler(_ref) {
  let {
    locale
  } = _ref;
  useEffect(() => {
    // Only run on client side to avoid hydration issues
    if (typeof window !== 'undefined') {
      // Validate locale against supported locales
      const supportedLocales = LOCALES.split(',');
      const validLocale = supportedLocales.includes(locale) ? locale : DEFAULT_LOCALE;

      // Get current stored locale before sync
      const currentStoredLocale = localStorage.getItem('locale');

      // Synchronize locale storage (localStorage + cookies)
      syncLocaleStorage(validLocale);

      // Set HTML lang attribute for accessibility and SEO
      if (document.documentElement.lang !== validLocale) {
        document.documentElement.lang = validLocale;
      }

      // Dispatch custom event for other components that might need to react to locale changes
      const localeChangeEvent = new CustomEvent('localeChange', {
        detail: {
          locale: validLocale,
          previousLocale: currentStoredLocale
        }
      });
      window.dispatchEvent(localeChangeEvent);

      // Log locale changes for debugging (remove in production if needed)
      if (currentStoredLocale && currentStoredLocale !== validLocale) {
        console.log("\uD83C\uDF10 Locale synchronized: ".concat(currentStoredLocale, " -> ").concat(validLocale));
      } else if (!currentStoredLocale) {
        console.log("\uD83C\uDF10 Locale initialized: ".concat(validLocale, " (no previous locale found)"));
        setStoredLocale(validLocale);
      }
    }
  }, [locale]);

  // This component doesn't render anything - it's purely for side effects
  return null;
}